import { ReactNode, useMemo } from 'react';

import { Modal } from '@atom/mui';

import BudgetAlignLoading from './BudgetAlignLoading';
import BudgetAlignMain from './BudgetAlignMain';
import BudgetAlignModalError from './BudgetAlignModalError';
import useBudgetAlignModal from './useBudgetAlignModal';

export interface Props {
  onClose: () => void;
}

export enum BudgetAlignView {
  MAIN,
  CONFIRM,
  LOADING,
  ERROR,
}

const BudgetAlignModal = ({ onClose }: Props) => {
  const { view, setView, handleSubmit, previewData, selectedItems } =
    useBudgetAlignModal({
      onClose,
    });

  const handleConfirmModal = () => {
    if (view === BudgetAlignView.ERROR) {
      onClose();
      return;
    }

    if (view === BudgetAlignView.MAIN) {
      setView(BudgetAlignView.CONFIRM);
      return;
    }

    handleSubmit();
  };

  const titles = useMemo<{ [key in BudgetAlignView]: React.ReactNode }>(
    () => ({
      [BudgetAlignView.MAIN]: 'Align Total Budget to Authorized Budget',
      [BudgetAlignView.CONFIRM]: 'Are You Sure?',
      [BudgetAlignView.LOADING]: 'Loading Alignment Preview',
      [BudgetAlignView.ERROR]: 'Error Aligning Budgets',
    }),
    [],
  );

  const content: { [key in BudgetAlignView]: ReactNode } = {
    [BudgetAlignView.MAIN]: (
      <BudgetAlignMain
        selectedItems={selectedItems}
        previewData={previewData}
      />
    ),
    [BudgetAlignView.CONFIRM]: `This change will be applied to ${previewData?.adjustedItemCount} budget items. Are you sure you want to make this change?`,
    [BudgetAlignView.LOADING]: <BudgetAlignLoading />,
    [BudgetAlignView.ERROR]: <BudgetAlignModalError />,
  };

  return (
    <Modal
      title={titles[view]}
      open
      onCancel={onClose}
      confirmButtonText={view === BudgetAlignView.ERROR ? 'OK' : 'Apply'}
      onConfirm={handleConfirmModal}
      onExited={onClose}
      data-cy="editBudgetModal"
      width="sm"
      disabled={view === BudgetAlignView.LOADING}
    >
      {content[view]}
    </Modal>
  );
};

export default BudgetAlignModal;
